import { z } from "zod";
import { normalizeTypeformUrlOrEmpty } from "@/lib/typeform";

export const LessonSchema = z.object({
  id: z.string().uuid(),
  title: z.string().min(1),
  motivations: z.array(z.string().min(1)).min(1),
  short_description: z.string().min(1),
  article: z.string().min(1),
  video_url: z.string().url().nullable().optional(),
  thumbnail_file: z.string().uuid().nullable().optional(),
  infographic_file: z.string().uuid().nullable().optional(),
  lesson_number: z.number().int().min(1),
  is_disabled: z.boolean().default(false),
  disabled_at: z.string().datetime().nullable().optional(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});

export const LessonInsertSchema = LessonSchema.pick({
  title: true,
  motivations: true,
  short_description: true,
  article: true,
  video_url: true,
  thumbnail_file: true,
  infographic_file: true,
  lesson_number: true,
  is_disabled: true,
}).partial({ is_disabled: true });

export const LessonUpdateSchema = LessonSchema.partial().extend({ id: z.string().uuid() });

export type Lesson = z.infer<typeof LessonSchema>;
export type LessonInsert = z.infer<typeof LessonInsertSchema>;
export type LessonUpdate = z.infer<typeof LessonUpdateSchema>;

// UI-only schema for Admin > Settings > Assessments
export const AssessmentFormSchema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string().min(1, "Description is required"),
  typeform_url: z
    .string()
    .trim()
    .refine(
      (value) => {
        const res = normalizeTypeformUrlOrEmpty(value);
        return res.ok;
      },
      {
        message:
          "Paste the public Typeform link from Share → Link (looks like https://form.typeform.com/to/<id>). Leave blank if not ready.",
      }
    ),
});
export type AssessmentForm = z.infer<typeof AssessmentFormSchema>;

// Onboarding summary enums and schema
export const GenderEnum = z.enum(["female", "male", "non-binary", "prefer-not-to-say"]);
export const MotivationEnum = z.enum([
  "lose-weight",
  "build-muscle",
  "improve-health",
  "boost-energy",
  "reduce-stress",
  "sport-performance",
]);
export const WorkoutFrequencyEnum = z.enum(["2-times", "3-times", "4-times", "5-plus"]);
export const ExerciseTypeEnum = z.enum([
  "strength-training",
  "cardio",
  "running",
  "cycling",
  "swimming",
  "group-classes",
  "sports",
  "hiit",
]);
export const TimelineEnum = z.enum(["1-month", "3-months", "6-months", "12-months"]);
export const AdditionalServiceEnum = z.enum(["find-gym", "workout-gear", "equipment", "gym-buddies"]);

export const HeightSchema = z.object({
  unit: z.enum(["imperial", "metric"]),
  feet: z.number().int().min(0).optional(),
  inches: z.number().int().min(0).max(11).optional(),
  cm: z.number().min(1).optional(),
}).superRefine((val, ctx) => {
  if (val.unit === "imperial") {
    if (typeof val.feet !== "number" && typeof val.inches !== "number") {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "feet or inches required for imperial" });
    }
  } else {
    if (typeof val.cm !== "number") {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "cm required for metric" });
    }
  }
});

export const WeightSchema = z.object({
  unit: z.enum(["lb", "kg"]),
  value: z.number().min(1),
});

export const OnboardingSummarySchema = z.object({
  gender: GenderEnum,
  dob: z.string().min(1), // ISO string preferred (yyyy-MM-dd)
  height: HeightSchema,
  weight: WeightSchema,
  motivations: z.array(MotivationEnum).max(2),
  workoutFrequency: WorkoutFrequencyEnum,
  exerciseTypes: z.array(ExerciseTypeEnum),
  timeline: TimelineEnum,
  additionalServices: z.array(AdditionalServiceEnum),
});
export type OnboardingSummary = z.infer<typeof OnboardingSummarySchema>;

// Legal documents
export const LegalDocType = z.enum(["terms", "privacy", "liability_waiver"]);

export const LegalDocumentSchema = z.object({
  id: z.string().uuid(),
  type: LegalDocType,
  title: z.string().min(1),
  version: z.number().int().min(1),
  content: z.string().min(1),
  created_at: z.string(),
});

export const AcceptedLegalDocumentSchema = z.object({
  document: LegalDocumentSchema,
  accepted_at: z.string(),
});

export type LegalDocType = z.infer<typeof LegalDocType>;
export type LegalDocument = z.infer<typeof LegalDocumentSchema>;
export type AcceptedLegalDocument = z.infer<typeof AcceptedLegalDocumentSchema>;
